import React, { useState } from "react";
import { createPortal } from "react-dom";
import classnames from "classnames";
import mergeRefs from "merge-refs";
import { usePopper } from "react-popper";

import { Icon } from "@formant/ui/component/common/Icon";
import { useToggle } from "@formant/ui/utils/useToggle";

import { getPortalElement } from "../../utils/getPortalElement";
import { useOnClickOutside } from "../../utils/hooks/useOnClickOutside";
import { Button } from "./Button";
import { IconButton } from "./IconButton";
import { Menu } from "./Menu";
import { IMenuItem } from "./Menu/IMenuItem";
import styles from "./PillSelect.scss";

export interface IPillSelectProps<T extends string | number | undefined> {
    title: string;
    label?: string;
    items: IMenuItem<T>[];
    empty?: boolean;
    disabled?: boolean;
    closeOnSelect?: boolean;
    className?: string;
    onClear: () => void;
    bottom?: boolean;
    immutable?: boolean;
    tooltipText?: string;
    menuStyle?: "blue" | "black";
}

export function PillSelect<T extends string | number | undefined>(
    props: IPillSelectProps<T>,
) {
    const {
        title,
        label,
        items,
        empty,
        disabled,
        closeOnSelect,
        className,
        onClear,
        immutable,
        tooltipText,
        bottom,
        menuStyle = "blue",
    } = props;

    const [open, { toggle: onClicked, off: onClose }] = useToggle(false);

    const clickOutsideRef = useOnClickOutside<HTMLDivElement>(onClose);
    const [refElement, setRefElement] = useState<HTMLElement | null>(null);
    const [popElement, setPopElement] = useState<HTMLDivElement | null>(null);

    const setTargetElement = mergeRefs<HTMLDivElement>(
        setPopElement,
        clickOutsideRef,
    );

    const context = usePopper(refElement, popElement, {
        placement: `${bottom ? "top" : "bottom"}-start`,
        modifiers: [
            { name: "flip" },
            { name: "offset", options: { offset: [0, 4] } },
        ],
    });

    const dropdown = !immutable && open && (
        <div
            ref={setTargetElement}
            {...context.attributes.popper}
            style={context.styles.popper}
        >
            <Menu
                visible={open}
                title={title}
                items={items}
                closeOnSelect={closeOnSelect}
                style={menuStyle}
                onClose={onClose}
                legacy={false}
            />
        </div>
    );

    return (
        <div
            className={classnames(
                styles["pill-select"],
                empty && styles.empty,
                immutable && styles.immutable,
                className,
            )}
        >
            <Button
                ref={setRefElement}
                onClicked={onClicked}
                className={styles.button}
                disabled={disabled}
            >
                <span data-tooltip={tooltipText}>{label || title}</span>
                {empty && <Icon name="chevron-down" size="18" />}
            </Button>
            {!empty && !immutable && (
                <IconButton
                    icon="close"
                    className={styles.clear}
                    onClick={onClear}
                />
            )}
            {createPortal(dropdown, getPortalElement())}
        </div>
    );
}
